import { useState, useEffect } from 'react';
import { reportService } from '../api/reports';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import FilePreview from '../components/ui/FilePreview';
import { useTranslation } from 'react-i18next';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';
import { Clock } from 'lucide-react';

export default function ViceReports() {
    const { t } = useTranslation();
    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal States
    const [selectedReport, setSelectedReport] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [actionType, setActionType] = useState(null); // 'approve' or 'reject'

    const [rejectComment, setRejectComment] = useState('');

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        try {
            const data = await reportService.getAll({ status: 'pending_vice' });
            setReports(data);
            setFilteredReports(data);
        } catch (error) {
            console.error("Failed to load reports", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = ({ search, dateFrom, dateTo }) => {
        let result = [...reports];

        if (search) {
            const lowerSearch = search.toLowerCase();
            result = result.filter(r =>
                r.title.toLowerCase().includes(lowerSearch) ||
                (r.author_name && r.author_name.toLowerCase().includes(lowerSearch))
            );
        }

        if (dateFrom) {
            result = result.filter(r => new Date(r.created_at) >= new Date(dateFrom));
        }

        if (dateTo) {
            const end = new Date(dateTo);
            end.setDate(end.getDate() + 1);
            result = result.filter(r => new Date(r.created_at) < end);
        }

        setFilteredReports(result);
    };

    const openConfirm = (report, type) => {
        setSelectedReport(report);
        setActionType(type);
        setConfirmOpen(true);
        setRejectComment('');
    };

    const handleConfirmAction = async () => {
        if (!selectedReport) return;

        try {
            await reportService.reviewVice(selectedReport.id, {
                action: actionType,
                comment: rejectComment
            });
            loadReports();
        } catch (error) {
            console.error("Review failed", error);
        }
    };

    const openPreview = (report) => {
        setSelectedReport(report);
        setPreviewOpen(true);
    };

    if (loading) return (
        <div className="flex justify-center items-center h-64 text-gray-500">
            <Clock className="w-6 h-6 animate-spin mr-2" /> Loading reports...
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('menu_final_check')}</h1>
                <p className="text-sm text-gray-500 mt-1">Финальное утверждение (Проректор)</p>
            </div>

            <ReportFilters onFilterChange={handleFilterChange} />

            <ReportTable
                reports={filteredReports}
                onPreview={openPreview}
                onApprove={(r) => openConfirm(r, 'approve')}
                onReject={(r) => openConfirm(r, 'reject')}
                role="vice"
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmAction}
                title={actionType === 'approve' ? 'Утвердить окончательно' : 'Отклонить отчет'}
                message={actionType === 'approve'
                    ? 'Отчет будет отправлен в Архив как утвержденный.'
                    : 'Пожалуйста, укажите причину отклонения отчета.'}
                confirmText={actionType === 'approve' ? 'Утвердить' : 'Отклонить'}
                variant={actionType === 'approve' ? 'primary' : 'danger'}
                showInput={actionType === 'reject'}
                inputPlaceholder="Укажите причину отклонения..."
                comment={rejectComment}
                onCommentChange={setRejectComment}
            />

            {/* File Preview Modal */}
            <FilePreview
                isOpen={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fileUrl={selectedReport?.file}
            />
        </div>
    );
}
